// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@eisinc/App/Components/Common/XngZone";
import CheckoutHeader from "@eisinc/App/Components/Header/CheckoutHeader";
import { getIsCheckoutPage } from "@eisinc/App/Store/Selectors/CheckoutSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const mapStateToProps = (state: ApplicationState) => ({
    isCheckoutPage: getIsCheckoutPage(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    // XNG-Changes
    linksWrapper?: InjectableCss;
    innerContainer?: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 20px 45px;
            margin: 0 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 78px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            padding: 20px 45px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        css`
                            flex-grow: 8;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkListHidden: {
        below: "lg",
    },
};

const LogoLinksSearch: React.FC<Props> = ({ id, isCheckoutPage }) => {
    if (isCheckoutPage) {
        return <CheckoutHeader widgetId={id} />;
    }

    // XNG-Changes: Changed structure
    return (
        <StyledWrapper {...logoLinksSearchStyles.container} data-test-selector="LogoLinksSearch">
            <StyledWrapper {...logoLinksSearchStyles.innerContainer}>
                <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                    <Zone contentId={id} zoneName="Logo" />
                </StyledWrapper>
                <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
                    <Zone contentId={id} zoneName="Search" />
                </StyledWrapper>
                <StyledWrapper {...logoLinksSearchStyles.linksWrapper}>
                    <XngZone contentId={id} zoneName="QuickOrder" />
                    <XngZone contentId={id} zoneName="RFQ" />
                    <XngZone contentId={id} zoneName="SalesRep" />
                    <XngZone contentId={id} zoneName="SignIn" />
                    <XngZone contentId={id} zoneName="Cart" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default widgetModule;
